import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
} from '@angular/core';

@Component({
  selector: 'app-modal',
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalComponent implements OnChanges, OnDestroy {
  // ── Inputs ────────────────────────────────────────────────────────────────
  @Input() titulo: string = 'Modal';
  @Input() mostrar: boolean = false;
  @Input() loading: boolean = false;
  @Input() btnAceptar: boolean = true;
  @Input() btnCerrar: boolean = true;
  @Input() disabledBtnAceptar: boolean = false;
  @Input() isArchivo: boolean = false;
  @Input() labelBtnAceptar: string = 'Aceptar';
  @Input() labelBtnCerrar: string = 'Cerrar';

  // ── Outputs ───────────────────────────────────────────────────────────────
  /** Two-way binding: notifica al padre que debe ocultar el modal. */
  @Output() mostrarChange = new EventEmitter<boolean>();
  /** El usuario confirmó la acción principal. */
  @Output() aceptar = new EventEmitter<void>();
  /** El modal fue cerrado sin confirmar (X o botón Cerrar). */
  @Output() cerrado = new EventEmitter<void>();

  // ── Estado interno ────────────────────────────────────────────────────────
  private _aceptando = false;

  constructor(private readonly renderer: Renderer2) {}

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnChanges(changes: SimpleChanges): void {
    const cambio = changes['mostrar'];
    if (!cambio) return;

    if (cambio.currentValue === true) {
      this.bloquearScroll();
    }

    if (cambio.previousValue === true && cambio.currentValue === false) {
      this.desbloquearScroll();

      if (!this._aceptando) {
        this.cerrado.emit();
      }

      this._aceptando = false;
    }
  }

  ngOnDestroy(): void {
    // Limpia la clase del body si el componente se destruye mientras está abierto.
    this.desbloquearScroll();
  }

  // ── Handlers de usuario ───────────────────────────────────────────────────

  onAceptar(): void {
    if (this.loading) return;
    this._aceptando = true;
    this.aceptar.emit();
  }

  onCerrar(): void {
    if (this.loading) return;
    if (!this.btnCerrar) return;
    this._aceptando = false;
    this.cerrarModal();
  }

  /** Cierra el modal al presionar Escape. */
  @HostListener('keydown.escape')
  onEscape(): void {
    if (this.mostrar && this.btnCerrar && !this.loading) {
      this.onCerrar();
    }
  }

  // ── Helpers privados ──────────────────────────────────────────────────────

  private cerrarModal(): void {
    this.mostrarChange.emit(false);
  }

  private bloquearScroll(): void {
    this.renderer.addClass(document.body, 'modal-open');
  }

  private desbloquearScroll(): void {
    this.renderer.removeClass(document.body, 'modal-open');
  }
}
